import { axiosInstance, useProxy } from 'api/axiosInstance';
import { staticRoutes } from 'api/apiRoutes';
import { IRelatedForResort, IWebcamItem } from 'api/region/types';
import {
  HistoricalSnowfallType,
  IContactInformation,
  IDescriptionsResort,
  IFirstHandReport,
  IResortPageInfo,
  IResortPageSnowInfo,
  IResortReports,
  IResortReviews,
  IResortStrengthsOverview,
  IResortTrailmapPage,
  IReviewsGeneral,
  ISnowfallItem,
  IWeatherInfo,
  ResortWeatherType,
  ReviewsOrderBy,
  IDailyResortLiftTickets,
  ISeasonResortLiftTickets, ISkiResortShop, IResortShortWeatherInfo,
} from 'api/resort/types';
import { IAccommodationPage, TAResponse3 } from 'api/tripadvisor/types';
import { getLocale } from 'i18n/locale';
import { ELocale } from 'i18n/localeEnum';
import { IS_NODE_ENV_DEVELOP } from 'ots-constants';
import { getPortalOptions } from 'util/getPortalConfig';
import { cacheProxy, corsProxy } from 'util/memcache';
import { INews } from 'api/home/types';
import { WidgetResortData, WidgetRegionData, ResortPhotosData } from 'components/widget/types';

export const get = async <T>(url: string) => {
  if (useProxy) {
    return cacheProxy<T>(url);
  }
  return axiosInstance().get<T>(url);
};

/**
 * @deprecated
 */
export const fetchResortStaticPathInfo = async () => [];
// (await get<IResortPathInfo[]>(staticRoutes.resortList())).data;

export const fetchResortLastFHReport = async (resortUUID: string) => {
  const { data } = await axiosInstance().get<IFirstHandReport>(staticRoutes.resortLastFirstHandReport(resortUUID));
  return data;
};

export const fetchResortSnowReport = async (resortUUID: string) => {
  const { data } = await axiosInstance().get<IResortPageSnowInfo>(staticRoutes.resortSnowReport(resortUUID));
  return data;
};

export const fetchResortShortInfo = async (resortUUID: string) => {
  const { data } = await axiosInstance().get<IResortPageInfo>(staticRoutes.resortShortInfo(resortUUID));
  return data;
};

export const fetchResortShortWeatherReport = async (resortUUID: string) => {
  const { data } = await axiosInstance().get<IResortShortWeatherInfo>(staticRoutes.resortShortWeatherInfo(resortUUID));
  return data;
};

export const fetchResortSkiRentals = async (resortUUID: string) => (
  await axiosInstance().get<Array<ISkiResortShop>>(staticRoutes.resortSkiRentalsAlpin(resortUUID))
).data;

export const fetchResortRelated = async (resortUUID: string) => (
  await axiosInstance().get<IRelatedForResort>(staticRoutes.resortRelated(resortUUID))
).data;

export const fetchResortFeaturedCam = async (resortUUID: string) => (
  await axiosInstance().get<IWebcamItem>(staticRoutes.resortFeaturedCam(resortUUID))
).data;

export const fetchResortCameras = async (resortUUID: string) => (
  await axiosInstance().get<{ data: Array<IWebcamItem> }>(staticRoutes.resortCameras(resortUUID))
).data.data;

export const fetchWeatherInfo = async (resortUUID: string, type: ResortWeatherType.daily | ResortWeatherType.hourly) => (
  await axiosInstance(type).get<IWeatherInfo>(staticRoutes.resortWeatherItems(resortUUID, type))
).data;

export const fetchSnowfallInfo = async (resortUUID: string, type: HistoricalSnowfallType.monthly | HistoricalSnowfallType.annual) => (
  await axiosInstance(type).get<{ snowfallItems: Array<ISnowfallItem> }>(staticRoutes.resortSnowfallItems(resortUUID, type))
).data.snowfallItems;

export const fetchResortTrailMap = async (resortUUID: string) => (
  await axiosInstance().get<IResortTrailmapPage>(staticRoutes.resortTrailMap(resortUUID))
).data;

export const fetchResortAccommodations = async (resortUUID: string) => (
  await axiosInstance().get<IAccommodationPage>(staticRoutes.resortAccomodations(resortUUID))
).data;

export const fetchResortRelatedNews = async (resortUUID: string, summer: boolean = false) => {
  const { hasNews } = getPortalOptions();
  if (hasNews) {
    const { data } = await axiosInstance().get<Array<INews>>(staticRoutes.resortRelatedNews(resortUUID, summer));
    return data;
  }
  return null;
};

export const TALangMap: Record<ELocale, string> = {
  [ELocale.ES_ES]: 'es',
  [ELocale.EN_US]: 'en',
  [ELocale.EN_GB]: 'en_UK',
  [ELocale.SV_SE]: 'sv',
  [ELocale.NL_NL]: 'nl',
  [ELocale.NN_NO]: 'no',
  [ELocale.DA_DK]: 'da',
  [ELocale.FR_FR]: 'fr',
  [ELocale.PL_PL]: 'pl',
  [ELocale.IT_IT]: 'it',
  [ELocale.CS_CZ]: 'cs',
  [ELocale.DE_DE]: 'de',
  [ELocale.SK_SK]: 'sk',
};

export const fetchTARealWithCorsHash = async (
  hotelIds: Array<string>,
  checkIn: string,
  checkOut: string,
  numAdults: number,
  numRooms: number,
  currency: string,
) => {
  // eslint-disable-next-line no-constant-condition
  if (false) {
    const url = 'https://www.tripadvisor.com/api/partner/3.0/synmeta-pricing';
    const params = {
      key: process.env.NEXT_PUBLIC_TRIP_ADVISOR_KEY,
      hotel_ids: hotelIds.sort().join(','),
      hotel_id_type: 'TA', // Trip Advisor
      user_agent: IS_NODE_ENV_DEVELOP ? navigator.userAgent : 'infer',
      ip_address: IS_NODE_ENV_DEVELOP ? '87.110.59.51' : 'infer',
      new_request: false,
      check_in: checkIn,
      check_out: checkOut,
      num_rooms: numRooms,
      num_adults: numAdults,
      currency,
    };

    const paramStr = Object.keys(params).map((k) => `${k}=${(params as any)[k]}`).join('&');

    return (await corsProxy<TAResponse3.Root>(`${url}?${paramStr}`)).data;
  }
  const params = {
    hotel_ids: hotelIds.sort().join(','),
    hotel_id_type: 'TA', // Trip Advisor
    new_request: false,
    check_in: checkIn,
    check_out: checkOut,
    num_rooms: numRooms,
    num_adults: numAdults,
    currency,
    lang: TALangMap[getLocale() as ELocale],
    locale: TALangMap[getLocale() as ELocale],
    __noCache: Date.now(),
  };

  const paramStr = Object.keys(params).map((k) => `${k}=${(params as any)[k]}`).join('&');

  return (await axiosInstance().get<TAResponse3.Root>(`${staticRoutes.pricing()}?${paramStr}`)).data;
};

export const fetchResortReports = async (regionId: string, page: number, limit: number) => (
  await axiosInstance(String(page)).get<IResortReports>(staticRoutes.resortReports(regionId, page, limit))
).data;

export const fetchResortReviewsPages = async (regionId: string, orderBy: ReviewsOrderBy, page: number) => (
  await axiosInstance(`${page}:${orderBy}`).get<IResortReviews>(staticRoutes.resortReviewsPages(regionId, page, orderBy))
).data;

export const fetchResortReviews = async (regionId: string) => (
  await axiosInstance().get<IReviewsGeneral>(staticRoutes.resortReviews(regionId))
).data;

export const fetchResortStrengths = async (regionId: string) => (
  await axiosInstance().get<IResortStrengthsOverview>(staticRoutes.resortStrengths(regionId))
).data;

export const fetchResortDescriptionTabs = async (regionId: string) => (
  await axiosInstance().get<Array<IDescriptionsResort>>(staticRoutes.resortDescriptionTabs(regionId))
).data;

export const fetchContactInformation = async (regionId: string) => (
  await axiosInstance().get<IContactInformation>(staticRoutes.contactInformation(regionId))
).data;

export const fetchResortDailyLiftTickets = async (resortId: string) => (await (
  axiosInstance().get<IDailyResortLiftTickets>(staticRoutes.resortDailyLiftTickets(resortId))
)).data;

export const fetchResortSeasonLiftTickets = async (resortId: string) => (await (
  axiosInstance().get<ISeasonResortLiftTickets>(staticRoutes.resortSeasonLiftTickets(resortId))
)).data;

export const fetchResortsForWidget = async (uuids: string) => (await (
  axiosInstance().get<WidgetResortData[]>(staticRoutes.getWidgetResorts(uuids))
)).data;

export const fetchRegionForWidget = async (regionId: string, flag?: string) => (await (
  axiosInstance().get<WidgetRegionData>(staticRoutes.getWidgetRegion(regionId, flag))
)).data;

export const fetchResortPhotos = async (resortId: string, summer: boolean = false) => (await (
  axiosInstance().get<ResortPhotosData>(staticRoutes.getResortPhotos(resortId, summer))
)).data;
